import { useMemo } from 'react';
import { isDataphonePayment } from '../../paymentModal/refundDataphoneVisibility';
import { PERMISSIONS } from '../../../lib/permissions';
import {
    LEGACY_PRIVILEGES,
    OperatorAccessWithPrivileges,
    canOrHasPrivilege,
} from '../../../lib/permissionOrPrivilege';

// Acciones de pago que entiende el backend al cancelar un alquiler. Espejo de
// `DELETING_PAYMENT_ACTION` en sporttia-service (`src/configs/constants.ts`);
// el frontend solo usa un subconjunto al cancelar reservas.
export const DELETING_PAYMENT_ACTION = {
    NOTHING: 0,
    DELETE_ALL: 2,
    DELETE_ONLY_PENDING: 3,
    REFUND_PURSE: 4,
    REFUND_CASH: 5,
    PAID_PURSE_PENDING_DELETE: 6,
    REFUND_DATAFONO: 7,
} as const;

// Traduce el `value` de la opción elegida en el desplegable (índice de fila,
// solo UI) a la acción de pago que espera el backend. Centraliza aquí el mapeo
// para no duplicarlo (ni sus números mágicos) en cada pantalla de cancelación.
// `NOTHING` se devuelve como `undefined` para que la llamada no envíe
// `paymentAction`, igual que el comportamiento previo de `cancelBooking()`.
export function paymentActionForOption(
    option: string | null,
): number | undefined {
    switch (option) {
        case '0':
            return undefined; // Dejar los pagos como están (NOTHING).
        case '1':
            return DELETING_PAYMENT_ACTION.REFUND_PURSE;
        case '2':
            return DELETING_PAYMENT_ACTION.REFUND_CASH;
        case '3':
            return DELETING_PAYMENT_ACTION.DELETE_ALL;
        case '4':
            return DELETING_PAYMENT_ACTION.DELETE_ONLY_PENDING;
        case '5':
            return DELETING_PAYMENT_ACTION.REFUND_DATAFONO;
        default:
            return undefined;
    }
}

type ImpersonalCancelOccupant = {
    impersonal?: boolean;
    paymentForm?: string | null;
};

export type ImpersonalCancelInfo = {
    // El alquiler contiene al menos una entrada impersonal (al portador).
    isImpersonal: boolean;
    // Mezcla entradas impersonales y nominales: no puede cancelarse completo,
    // hay que cancelar cada entrada por separado.
    isMixedImpersonal: boolean;
    // El datáfono solo puede ofrecerse si el cobro original se hizo con datáfono.
    // Al cancelar el alquiler completo la devolución se aplica a todas las
    // entradas, así que solo se permite cuando todas las impersonales se
    // cobraron con datáfono.
    canRefundDataphone: boolean;
};

/**
 * Deriva, a partir de la lista de ocupantes, cómo debe comportarse la
 * cancelación de un alquiler con entradas impersonales: las impersonales solo
 * pueden devolverse en efectivo o datáfono (nunca monedero) y un alquiler mixto
 * no puede cancelarse completo. Función pura para poder testearla sin React.
 */
export function getImpersonalCancelInfo(
    occupants: ImpersonalCancelOccupant[] | undefined | null,
): ImpersonalCancelInfo {
    const list = occupants ?? [];
    const hasImpersonal = list.some(
        (occupant) => occupant?.impersonal === true,
    );
    const hasNominal = list.some((occupant) => occupant?.impersonal !== true);
    const impersonalOccupants = list.filter(
        (occupant) => occupant?.impersonal === true,
    );

    return {
        isImpersonal: hasImpersonal,
        isMixedImpersonal: hasImpersonal && hasNominal,
        canRefundDataphone:
            impersonalOccupants.length > 0 &&
            impersonalOccupants.every((occupant) =>
                isDataphonePayment(occupant?.paymentForm ?? undefined),
            ),
    };
}

/**
 * Envoltorio memoizado de {@link getImpersonalCancelInfo} para componentes.
 * Recalcula solo cuando cambia la referencia de `occupants`.
 */
export function useImpersonalCancelInfo(
    occupants: ImpersonalCancelOccupant[] | undefined | null,
): ImpersonalCancelInfo {
    return useMemo(() => getImpersonalCancelInfo(occupants), [occupants]);
}

export type CancelBookingPaymentOption = { caption: string; value: string };

/**
 * Opciones del desplegable «¿qué hacer con los pagos?» al cancelar un
 * alquiler. «Borrar» (3) acaba en `paymentService.deletePayment()` también
 * para los pagos cobrados, así que solo se ofrece si `canDeletePayments`:
 * ofrecerla a quien no puede borrar pagos produce un 403 del que el diálogo no
 * se recupera. «Pendiente» (4) se ofrece siempre: solo borra pagos sin cobrar,
 * y el API no pide el permiso para borrar un pago `NOT_PAID`. El booleano lo
 * calcula quien llama con {@link canDeleteBookingPayments}.
 */
export function buildCancelBookingPaymentOptions(
    canDeletePayments: boolean,
    t: (key: string) => string,
    isImpersonal = false,
    canRefundDataphone = false,
): CancelBookingPaymentOption[] {
    // Las entradas impersonales se cobran bajo el usuario invitado, que no tiene
    // monedero útil; su devolución solo puede ir en efectivo o datáfono (nunca
    // monedero). El backend rechaza cualquier otra acción con 400, así que aquí
    // ofrecemos únicamente esas dos formas.
    if (isImpersonal) {
        const options: CancelBookingPaymentOption[] = [
            { caption: t('impersonalTicket.refundCash'), value: '2' },
        ];

        // El datáfono solo se ofrece si el cobro original se hizo con datáfono;
        // el efectivo siempre está disponible.
        if (canRefundDataphone) {
            options.push({
                caption: t('impersonalTicket.refundDatafono'),
                value: '5',
            });
        }

        return options;
    }

    const baseOptions: CancelBookingPaymentOption[] = [
        { caption: t('associatedPayments.doNothing'), value: '0' },
        { caption: t('associatedPayments.refundToPurse'), value: '1' },
        { caption: t('associatedPayments.refundInCash'), value: '2' },
    ];
    const pendingOption: CancelBookingPaymentOption = {
        caption: t('associatedPayments.pending'),
        value: '4',
    };

    if (!canDeletePayments) return [...baseOptions, pendingOption];

    return [
        ...baseOptions,
        { caption: t('associatedPayments.delete'), value: '3' },
        pendingOption,
    ];
}

/**
 * Si el operador puede borrar pagos al cancelar un alquiler: con los roles v8
 * encendidos, `accounting.delete`; con los roles apagados, el privilegio
 * DELETE_PAYMENT (28) de sporttia-service, como siempre. `AppContext` cumple
 * la forma de `access`.
 */
export function canDeleteBookingPayments(
    access: OperatorAccessWithPrivileges,
): boolean {
    return canOrHasPrivilege(
        access,
        PERMISSIONS.ACCOUNTING.DELETE,
        LEGACY_PRIVILEGES.DELETE_PAYMENT,
    );
}
